import {
  AutoAwesome,
  Close,
  Download,
  Refresh,
  Send,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import html2canvas from "html2canvas";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  isStreaming?: boolean;
}

// Common aspect ratios for image templates
const ASPECT_RATIOS = [
  { label: "Square (1:1)", width: 1024, height: 1024 },
  { label: "Landscape (16:9)", width: 1024, height: 576 },
  { label: "Portrait (9:16)", width: 576, height: 1024 },
  { label: "Instagram Post (4:5)", width: 1024, height: 1280 },
  { label: "Instagram Story (9:16)", width: 1080, height: 1920 },
  { label: "Facebook Post (1.91:1)", width: 1200, height: 628 },
  { label: "Twitter Post (16:9)", width: 1200, height: 675 },
  { label: "LinkedIn Post (1.91:1)", width: 1200, height: 627 },
];

interface BrandImage {
  url: string;
  name?: string;
  description?: string;
}

interface FrameBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  onExport: (pngUrl: string, name: string, width: number, height: number) => void;
  previewImageUrl?: string; // Optional background image to show behind the frame
  brandImages?: BrandImage[]; // Brand images (logos, products, etc.) for the AI to use
  imageStylePrompt?: string; // Style prompt for the brand
}

const FrameBuilderDialog: React.FC<FrameBuilderDialogProps> = ({
  open,
  onClose,
  companyId,
  companyName,
  getToken,
  eventListener,
  onExport,
  previewImageUrl,
  brandImages,
  imageStylePrompt,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [frameName, setFrameName] = useState("New Frame");
  const [selectedRatio, setSelectedRatio] = useState(ASPECT_RATIOS[0]);
  const [htmlContent, setHtmlContent] = useState("");
  const [prompt, setPrompt] = useState("");
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setFrameName("New Frame");
      setSelectedRatio(ASPECT_RATIOS[0]);
      setHtmlContent("");
      setPrompt("");
      setChat([]);
      setChatLoading(false);
      setExporting(false);
    }
  }, [open]);

  // Check if response contains valid HTML (backend now extracts it)
  const isValidHtml = (response: string): boolean => {
    const trimmed = response.trim();
    return trimmed.includes("<!DOCTYPE") || trimmed.includes("<html") || trimmed.includes("<body");
  };

  const handleSendMessage = async () => {
    if (!prompt.trim() || chatLoading) return;

    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: "user",
      content: prompt,
      timestamp: Date.now(),
    };

    setChat((prev) => [...prev, userMessage]);
    const currentPrompt = prompt;
    setPrompt("");
    setChatLoading(true);

    // Create a placeholder for the streaming response
    const assistantMessageId = uuidv4();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: "assistant",
      content: "",
      timestamp: Date.now(),
      isStreaming: true,
    };
    setChat((prev) => [...prev, assistantMessage]);

    try {
      const token = await getToken();

      const response = await fetch(
        `${hostname}/content/frame-builder/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            companyId,
            message: currentPrompt,
            messages: chat.map((m) => ({ role: m.role, content: m.content })),
            frameName,
            frameWidth: selectedRatio.width,
            frameHeight: selectedRatio.height,
            currentHtml: htmlContent || undefined,
            brandImages: brandImages || undefined,
            imageStylePrompt: imageStylePrompt || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send message");
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let fullResponse = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        fullResponse += chunk;

        setChat((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId ? { ...m, content: fullResponse } : m
          )
        );
      }

      // Backend now extracts clean HTML, just validate and set it
      if (isValidHtml(fullResponse)) {
        console.log("Setting htmlContent, length:", fullResponse.length);
        setHtmlContent(fullResponse);
        setPreviewKey((k) => k + 1);
      } else {
        console.log("Response doesn't look like valid HTML:", fullResponse.substring(0, 100));
      }

      // Mark streaming as complete
      setChat((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId ? { ...m, isStreaming: false } : m
        )
      );
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
      setChat((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId
            ? {
                ...m,
                content: "Sorry, I encountered an error. Please try again.",
                isStreaming: false,
              }
            : m
        )
      );
    } finally {
      setChatLoading(false);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chat]);

  const handleExportPng = async () => {
    if (!htmlContent) {
      eventListener({
        type: "SET_ERROR",
        error: "No frame content to export. Build a frame first.",
      });
      return;
    }

    setExporting(true);

    try {
      // Create a temporary container for rendering
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.width = `${selectedRatio.width}px`;
      container.style.height = `${selectedRatio.height}px`;
      container.style.overflow = "hidden";
      document.body.appendChild(container);

      // Create an iframe to render the HTML with styles
      const iframe = document.createElement("iframe");
      iframe.style.width = `${selectedRatio.width}px`;
      iframe.style.height = `${selectedRatio.height}px`;
      iframe.style.border = "none";
      iframe.style.margin = "0";
      iframe.style.padding = "0";
      iframe.style.display = "block";
      container.appendChild(iframe);

      // Wait for iframe to be ready
      await new Promise<void>((resolve) => {
        iframe.onload = () => resolve();
        iframe.srcdoc = htmlContent;
      });

      // Wait a bit more for styles to load
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Capture the iframe content
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDocument?.body) {
        throw new Error("Could not access iframe content");
      }

      // Reset any potential margins on the iframe's body and html
      iframeDocument.documentElement.style.cssText = "margin:0;padding:0;width:100%;height:100%;";
      iframeDocument.body.style.cssText = "margin:0;padding:0;width:100%;height:100%;position:relative;overflow:hidden;";

      const canvas = await html2canvas(iframeDocument.documentElement, {
        backgroundColor: null, // Transparent background
        width: selectedRatio.width,
        height: selectedRatio.height,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
        scale: 1,
        useCORS: true,
        allowTaint: true,
        windowWidth: selectedRatio.width,
        windowHeight: selectedRatio.height,
      });

      // Convert to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (b) => {
            if (b) resolve(b);
            else reject(new Error("Failed to create blob"));
          },
          "image/png",
          1.0
        );
      });

      // Clean up
      document.body.removeChild(container);

      // Upload to platform-images
      const token = await getToken();
      const formData = new FormData();
      formData.append("image", blob, `${frameName.replace(/\s+/g, "_")}.png`);
      formData.append("name", frameName);
      formData.append("category", "brand_template");
      formData.append("companyId", companyId);
      formData.append("public", "true");

      const uploadResponse = await axios.post(
        `${hostname}/platform-images`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (uploadResponse.data?.url) {
        onExport(
          uploadResponse.data.url,
          frameName,
          selectedRatio.width,
          selectedRatio.height
        );
        eventListener({
          type: "SET_SUCCESS",
          message: "Frame exported and added as template!",
        });
        onClose();
      }
    } catch (error) {
      console.error("Export error:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setExporting(false);
    }
  };

  // Calculate the preview dimensions (max 600px width, maintaining aspect ratio)
  const getPreviewDimensions = useCallback(() => {
    const maxWidth = 600;
    const scale = Math.min(1, maxWidth / selectedRatio.width);
    return {
      width: selectedRatio.width * scale,
      height: selectedRatio.height * scale,
    };
  }, [selectedRatio]);

  // The HTML is now responsive (uses 100% width/height), so we just return it directly
  const getPreviewHtml = useCallback(() => {
    return htmlContent;
  }, [htmlContent]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AutoAwesome color="primary" />
          <TextField
            size="small"
            value={frameName}
            onChange={(e) => setFrameName(e.target.value)}
            placeholder="Frame Name"
            sx={{ width: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Aspect Ratio</InputLabel>
            <Select
              value={ASPECT_RATIOS.findIndex(
                (r) =>
                  r.width === selectedRatio.width &&
                  r.height === selectedRatio.height
              )}
              label="Aspect Ratio"
              onChange={(e) => setSelectedRatio(ASPECT_RATIOS[e.target.value as number])}
            >
              {ASPECT_RATIOS.map((ratio, index) => (
                <MenuItem key={index} value={index}>
                  {ratio.label} ({ratio.width}x{ratio.height})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LoadingButton
            variant="contained"
            startIcon={<Download />}
            onClick={handleExportPng}
            loading={exporting}
            disabled={!htmlContent}
          >
            Export & Add Template
          </LoadingButton>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ height: "100%", display: "flex" }}>
          {/* Left Pane - Chat */}
          <Box
            sx={{
              width: isMobile ? "100%" : "40%",
              minWidth: 320,
              maxWidth: 500,
              display: "flex",
              flexDirection: "column",
              borderRight: 1,
              borderColor: "divider",
            }}
          >
            {/* Chat Messages */}
            <Box
              ref={chatContainerRef}
              sx={{ flex: 1, overflow: "auto", p: 2 }}
            >
              {chat.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary" gutterBottom>
                    Build your frame overlay with AI
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Describe the frame you want to create. The frame will overlay
                    on AI-generated images.
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Try prompts like:
                    </Typography>
                    <Chip
                      label="Instagram story with logo bottom-right"
                      size="small"
                      sx={{ m: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        setPrompt(
                          `Create an Instagram story frame for ${companyName} with the company name in a stylish font at the bottom-right corner and a subtle gradient bar at the bottom`
                        )
                      }
                    />
                    <Chip
                      label="Sale announcement frame"
                      size="small"
                      sx={{ m: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        setPrompt(
                          "Create a bold sale announcement frame with a 'SALE' banner in the top-left corner and space for discount percentage"
                        )
                      }
                    />
                    <Chip
                      label="Professional watermark"
                      size="small"
                      sx={{ m: 0.5, cursor: "pointer" }}
                      onClick={() =>
                        setPrompt(
                          `Create a subtle professional watermark frame with ${companyName} in the bottom-right corner with slight transparency`
                        )
                      }
                    />
                  </Box>
                </Box>
              ) : (
                chat.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor:
                        message.role === "user"
                          ? "action.hover"
                          : "background.paper",
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: "block" }}
                    >
                      {message.role === "user" ? "You" : "AI Designer"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        "& code": {
                          bgcolor: "action.hover",
                          px: 0.5,
                          borderRadius: 0.5,
                          fontFamily: "monospace",
                          fontSize: "0.85em",
                        },
                      }}
                    >
                      {message.content}
                    </Typography>
                  </Box>
                ))
              )}
              {chatLoading && (
                <Box display="flex" alignItems="center" gap={1} p={2}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Designing...
                  </Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Chat Input */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe your frame design..."
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={chatLoading}
              />
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <LoadingButton
                  variant="contained"
                  onClick={handleSendMessage}
                  loading={chatLoading}
                  disabled={!prompt.trim()}
                  startIcon={<Send />}
                >
                  Send
                </LoadingButton>
              </Box>
            </Box>
          </Box>

          {/* Right Pane - Preview */}
          {!isMobile && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default",
              }}
            >
              {/* Preview Header */}
              <Box
                sx={{
                  p: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  Preview ({selectedRatio.width}x{selectedRatio.height})
                </Typography>
                <Tooltip title="Refresh Preview">
                  <IconButton
                    size="small"
                    onClick={() => setPreviewKey((k) => k + 1)}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Preview Area */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  overflow: "auto",
                  // Checkered background to show transparency
                  backgroundImage: `
                    linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
                    linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
                    linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
                    linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
                  `,
                  backgroundSize: "20px 20px",
                  backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                }}
              >
                {htmlContent ? (
                  <Box
                    sx={{
                      position: "relative",
                      boxShadow: 3,
                    }}
                  >
                    <iframe
                      key={previewKey}
                      ref={iframeRef}
                      title="Frame Preview"
                      srcDoc={getPreviewHtml()}
                      style={{
                        display: "block", // Removes the inline gap at the bottom
                        width: getPreviewDimensions().width,
                        height: getPreviewDimensions().height,
                        border: "none",
                        backgroundColor: "transparent",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: getPreviewDimensions().width,
                      height: getPreviewDimensions().height,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: 2,
                      borderStyle: "dashed",
                      borderColor: "divider",
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography color="text.secondary" textAlign="center">
                      Start chatting to design your frame.
                      <br />
                      The transparent areas will show the image beneath.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FrameBuilderDialog;
